import { Minus } from "lucide-react";

import { reais } from "../lib/caixa";

// Contagem do dinheiro na abertura do caixa (dono, 24/09/2026: "está muito
// feia, deixe mais intuitiva e bonita"). Cada nota é um cartão na cor da
// cédula de verdade; tocar soma uma. Cores em hex inline: as classes
// orange-* viram a cor da loja (index.css).
export const NOTAS = [
  { valor: 200, fundo: "linear-gradient(135deg,#a8a29e,#57534e)", texto: "#fff" },
  { valor: 100, fundo: "linear-gradient(135deg,#38bdf8,#1d4ed8)", texto: "#fff" },
  { valor: 50, fundo: "linear-gradient(135deg,#f59e0b,#9a3412)", texto: "#fff" },
  { valor: 20, fundo: "linear-gradient(135deg,#fde047,#eab308)", texto: "#422006" },
  { valor: 10, fundo: "linear-gradient(135deg,#fb7185,#be123c)", texto: "#fff" },
  { valor: 5, fundo: "linear-gradient(135deg,#c084fc,#7e22ce)", texto: "#fff" },
  { valor: 2, fundo: "linear-gradient(135deg,#60a5fa,#334155)", texto: "#fff" },
];

export const MOEDAS = [
  { valor: 1, rotulo: "1,00", fundo: "radial-gradient(circle,#fcd34d 0 44%,#a1a1aa 46%,#e4e4e7 70%,#a1a1aa 100%)" },
  { valor: 0.5, rotulo: "0,50", fundo: "radial-gradient(circle at 30% 30%,#fafafa,#a1a1aa)" },
  { valor: 0.25, rotulo: "0,25", fundo: "radial-gradient(circle at 30% 30%,#fde68a,#ca8a04)" },
  { valor: 0.1, rotulo: "0,10", fundo: "radial-gradient(circle at 30% 30%,#fef3c7,#d97706)" },
  { valor: 0.05, rotulo: "0,05", fundo: "radial-gradient(circle at 30% 30%,#fdba74,#9a3412)" },
];

// Soma em centavos: 3 × 0,10 não pode virar 0,30000000000000004
export const somaContagem = (qtd) =>
  [...NOTAS, ...MOEDAS].reduce((s, c) => s + (qtd[c.valor] || 0) * Math.round(c.valor * 100), 0) / 100;

export const contarPecas = (qtd, lista) => lista.reduce((s, c) => s + (qtd[c.valor] || 0), 0);

const lerQtd = (texto) => Math.min(9999, parseInt(String(texto).replace(/\D/g, ""), 10) || 0);

function Nota({ nota, qtd, set }) {
  const mais = () => set(qtd + 1);
  return (
    <div
      role="button" tabIndex={0} onClick={mais}
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); mais(); } }}
      className={`relative overflow-hidden select-none rounded-xl p-2.5 h-[104px] flex flex-col justify-between shadow-sm cursor-pointer transition active:scale-[0.97] ${qtd ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : "hover:brightness-110"}`}
      style={{ background: nota.fundo, color: nota.texto }}
      aria-label={`Nota de ${reais(nota.valor)}: ${qtd}. Toque para somar uma`}
      data-e2e={`nota-${nota.valor}`}
    >
      <div className="relative flex items-start justify-between gap-1">
        <span className="text-lg font-black leading-none">R$ {nota.valor}</span>
        {qtd ? <span className="rounded-full bg-white/90 text-zinc-900 text-[11px] font-black px-1.5 py-0.5 tabular-nums leading-none">×{qtd}</span> : null}
      </div>
      <span className="relative text-[11px] font-semibold opacity-90 tabular-nums whitespace-nowrap truncate">{qtd ? reais(qtd * nota.valor) : "toque = +1"}</span>
      <div className="relative flex items-center gap-1.5">
        <button type="button" disabled={!qtd} aria-label={`Tirar uma nota de ${reais(nota.valor)}`}
          onClick={(e) => { e.stopPropagation(); set(Math.max(0, qtd - 1)); }}
          className={`w-8 h-8 rounded-lg bg-black/25 flex items-center justify-center ${qtd ? "" : "invisible"}`}>
          <Minus className="w-4 h-4" />
        </button>
        <input type="text" inputMode="numeric" value={qtd || ""} placeholder="0"
          onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}
          onFocus={(e) => e.target.select()} onChange={(e) => set(lerQtd(e.target.value))}
          aria-label={`Quantidade de notas de ${reais(nota.valor)}`} data-e2e={`qtd-nota-${nota.valor}`}
          className="w-12 h-8 rounded-lg bg-black/25 text-center text-sm font-bold tabular-nums outline-none placeholder:text-current placeholder:opacity-60 focus:ring-2 focus:ring-white/80" />
      </div>
    </div>
  );
}

function Moeda({ moeda, qtd, set }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative">
        <button type="button" onClick={() => set(qtd + 1)} data-e2e={`moeda-${moeda.rotulo}`}
          aria-label={`Moeda de ${moeda.rotulo}: ${qtd}. Toque para somar uma`}
          className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-md text-[11px] sm:text-xs font-black text-zinc-900 flex items-center justify-center transition active:scale-95 ${qtd ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : "hover:brightness-110"}`}
          style={{ background: moeda.fundo }}>
          {moeda.rotulo}
        </button>
        {qtd ? (
          <button type="button" onClick={() => set(qtd - 1)} aria-label={`Tirar uma moeda de ${moeda.rotulo}`}
            className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-background border shadow flex items-center justify-center">
            <Minus className="w-3 h-3" />
          </button>
        ) : null}
      </div>
      <input type="text" inputMode="numeric" value={qtd || ""} placeholder="0"
        onFocus={(e) => e.target.select()} onChange={(e) => set(lerQtd(e.target.value))}
        aria-label={`Quantidade de moedas de ${moeda.rotulo}`} data-e2e={`qtd-moeda-${moeda.rotulo}`}
        className="w-11 h-7 rounded-md border bg-muted/40 text-center text-xs font-bold tabular-nums outline-none focus:ring-2 focus:ring-primary" />
    </div>
  );
}

/** Notas (cartões) e moedas (círculos); `qtd` = { [valor]: quantidade }. */
export default function ContagemNotas({ qtd, setQtd }) {
  const set = (valor) => (n) => setQtd((p) => ({ ...p, [valor]: Math.max(0, n) }));
  return (
    <div className="space-y-5">
      <section>
        <div className="flex items-baseline justify-between mb-2">
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Notas</p>
          <p className="text-[11px] text-muted-foreground">toque na nota para somar uma</p>
        </div>
        <div className="grid grid-cols-2 min-[380px]:grid-cols-3 gap-2.5">
          {NOTAS.map((n) => <Nota key={n.valor} nota={n} qtd={qtd[n.valor] || 0} set={set(n.valor)} />)}
        </div>
      </section>
      <section>
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Moedas</p>
        <div className="grid grid-cols-5 gap-1 pt-1">
          {MOEDAS.map((m) => <Moeda key={m.valor} moeda={m} qtd={qtd[m.valor] || 0} set={set(m.valor)} />)}
        </div>
      </section>
    </div>
  );
}
